import type {
  BlockStateDefinition,
  ImplicitDependencyGroup,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { BlockType, ComponentTypeUnionType } from '@unifyapps/defs/types/block';
import type { FiltersBlockStateType, FiltersComponentType } from '@unifyapps/defs/types/blocks';
import _pick from 'lodash/pick';
import { lazy } from 'react';
import type { Action } from '@unifyapps/defs/types/action';
import { isEmpty, omit } from 'lodash';

const importFiltersComponent = () =>
  import(
    /* webpackChunkName: "filters-block" */
    './Filters'
  );

const Filters = lazy(importFiltersComponent);

class FiltersBlockStateDefinition implements BlockStateDefinition {
  getComponent() {
    return Filters;
  }

  get type(): ComponentTypeUnionType {
    return 'Filters';
  }

  get initialStateGetter() {
    return (block: BlockType) => {
      const blockState: FiltersBlockStateType = {
        id: block.id,
        ..._pick(block.component as FiltersComponentType, [
          'content',
          'appearance',
          'componentType',
        ]),
      };
      return blockState;
    };
  }

  getBlockControlMethods() {
    return [
      {
        id: 'reset' as const,
        label: 'Reset',
        method: (blockState: FiltersBlockStateType, action: Action) => {
          const { methodPayload } = action.payload as {
            methodPayload: { fields: string[] };
          };

          const { fields } = methodPayload;

          const keysToOmit: string[] = [];

          if (fields.includes('filter')) {
            keysToOmit.push(
              'filter',
              'dateTimeFilter',
              'dateRangePreset',
              'dataQuickFilter',
              'dateQuickFilter',
              'fieldFilters',
            );
          }

          if (fields.includes('sort')) {
            keysToOmit.push('sort');
          }

          if (fields.includes('search')) {
            keysToOmit.push('search');
          }

          const newState = omit(blockState.state, keysToOmit);

          return {
            ...blockState,
            state: isEmpty(newState) ? undefined : newState,
          } as FiltersBlockStateType;
        },
      },
    ];
  }

  getImplicitDependencyGroups(): ImplicitDependencyGroup {
    return { usingDataSourcesResponse: true, isEntitySuspended: true };
  }

  preload() {
    return {
      blockPreload: importFiltersComponent,
    };
  }
}

export default FiltersBlockStateDefinition;
